<template>
  <vetster-input-group
    :id="getId"
    :label="label"
    :description-label="descriptionLabel"
    :disabled="disabled"
    :shake="shake"
    :error="error"
    :feedback="feedback || null"
  >
    <div
      class="relative"
      :class="noMargin ? '' : 'mt-1'"
    >
      <select
        :id="getId"
        ref="select"
        v-model="currentValue"
        :autofocus="autofocus"
        :disabled="disabled"
        :name="name"
        :required="required"
        :multiple="multiple"
        :class="currentClass"
        class="block w-full font-normal text-base leading-5 border-0 p-3 pr-8 ring-1 ring-inset ring-neutral-200 placeholder:text-neutral-400 transition"
        @blur="onBlur"
        @focus="onFocus"
      >
        <template v-for="(option, index) in normalizedOptionsWithPlaceholder">
          <optgroup
            v-if="option.children"
            :key="`${option.value}-optgroup-${index}`"
            :label="option.text"
          >
            <option
              v-for="(childOption, index2) in option.children"
              :key="`${childOption.value}-${index}-${index2}`"
              :value="childOption.value"
              :disabled="isPlaceholder(option)"
              v-text="childOption.text"
            />
          </optgroup>
          <option
            v-else
            :key="`${option.value}-${index}`"
            :value="option.value"
            :disabled="isPlaceholder(option)"
            v-text="option.text"
          />
        </template>
      </select>
      <div
        v-if="error || reset"
        class="absolute inset-y-0 right-5 pr-3 flex items-center"
      >
        <fa
          v-if="error"
          aria-hidden="true"
          :icon="['fas', 'exclamation-circle']"
          class="fa-fw text-base text-red-600"
        />
        <button
          v-if="reset"
          type="button"
          :class="{ 'text-red-600 ml-1': error }"
          class="cursor-pointer w-3"
          @click.prevent="currentValue = ''"
        >
          <fa :icon="['fal', 'times']" />
        </button>
      </div>
    </div>
  </vetster-input-group>
</template>

<script>
// This component needs to be rebranded fully based on the design system
// for now only modifying most basic elements
import { library } from '@fortawesome/fontawesome-svg-core';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import get from 'lodash/get';

import VetsterInputGroup from '@/brand-components/VetsterInputGroup';
import { uuid } from '@/helpers/uuid';
import CommonAttributes from '@/mixins/CommonAttributes';

library.add(faExclamationCircle);

export default {
  name: 'VetsterSelect',

  components: { VetsterInputGroup },

  mixins: [CommonAttributes],

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    autocomplete: {
      type: String,
      default: null,
    },
    descriptionLabel: {
      type: String,
      default: null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: String,
      default: null,
    },
    isRebranded: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    modelValue: {
      type: [String, Number, Array, Object],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    options: {
      type: [Array, Object],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    reset: {
      type: Boolean,
      default: false,
    },
    shake: {
      type: Boolean,
      default: false,
    },
    textAttribute: {
      type: String,
      default: undefined,
    },
    valueAttribute: {
      type: String,
      default: undefined,
    },
  },

  emits: [
    'blur',
    'change',
    'focus',
    'update:modelValue',
  ],

  data() {
    return {
      currentValue: this.modelValue,
    };
  },

  computed: {
    currentClass() {
      return {
        'opacity-40': this.disabled,
        'border-red-600 ring-red-600 text-red-600 focus:border-red-600 placeholder-red-600 focus:ring-red-600': this.error,
        'focus:ring-blue-900 focus:ring-2 text-neutral-900': !this.isRebranded && !this.error,
        'h-11 placeholder:text-base ring-neutral-200 focus:ring-ocean-600 text-neutral-900 rounded-lg': this.isRebranded,
        'text-base placeholder:text-neutral-500 ring-neutral-400 focus:ring-2 focus:ring-inset': !this.isRebranded,
      };
    },
    getId() {
      return this.id || uuid();
    },
    normalizedOptionsWithPlaceholder() {
      if (this.placeholder === undefined || !this.placeholder) {
        return this.normalizedOptions;
      }

      const { normalizedOptions } = this;
      normalizedOptions.unshift({
        value: null,
        text: this.placeholder,
      });

      return normalizedOptions;
    },
    normalizedOptions() {
      if (Array.isArray(this.options)) {
        return this.options.map((option) => this.normalizeOption(option));
      }

      return Object.keys(this.options).map((key) => ({
        value: key,
        text: this.options[key],
      }));
    },
  },

  watch: {
    currentValue(value) {
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
    modelValue(value) {
      this.currentValue = value;
    },
  },

  methods: {
    blur() {
      this.$refs.select.blur();
    },
    focus() {
      this.$refs.select.focus();
    },
    guessOptionText(option) {
      if (this.textAttribute) {
        return get(option, this.textAttribute);
      }

      return get(option, 'text', get(option, 'label'));
    },
    guessOptionValue(option) {
      if (this.valueAttribute) {
        return get(option, this.valueAttribute);
      }

      return get(option, 'value', get(option, 'id', get(option, 'text')));
    },
    isPlaceholder(option) {
      return option.value == null && option.text === this.placeholder;
    },
    onBlur(e) {
      this.$emit('blur', e);
    },
    onFocus(e) {
      this.$emit('focus', e);
    },
    normalizeOption(option) {
      if (['string', 'number', 'boolean'].includes(typeof option)) {
        return {
          value: option,
          text: option,
        };
      }

      if (option.children && Array.isArray(option.children)) {
        return {
          value: this.guessOptionValue(option),
          text: this.guessOptionText(option),
          children: option.children.map((childOption) => this.normalizeOption(childOption)),
        };
      }

      return {
        value: this.guessOptionValue(option),
        text: this.guessOptionText(option),
      };
    },
  },
};
</script>
